/**
 * Calendar / CalendarRegistry adapter wrapping gantt's calendar subsystem.
 *
 * Implements `05-` §C.2 with the D-21 direction mapping and D-22 weakened
 * round-trip contract.
 *
 * The adapter is a thin wrapper — it does NOT reimplement working-time
 * arithmetic. All work goes through gantt's native
 * `calculateEndDate` / `calculateDuration` and the per-calendar
 * `getClosestWorkTime`. Engine extraction (design choice 9, Phase F) would
 * replace this adapter with an equivalent backed by a standalone calendar
 * implementation; engine semantics remain unchanged.
 */

import type {
  Calendar,
  CalendarRegistry,
  CalendarID,
  NormalizedTaskID,
  TaskID,
  SnapDirection,
  TimeUnit,
  HierarchyView
} from "../core/types";
import { normalizeTaskId, taskIdLookupCandidates } from "../core/ids";

/**
 * Minimal subset of the gantt instance our calendar adapter touches. We
 * accept a structural interface rather than the full Gantt type so adapter
 * tests can pass a stub.
 */
export interface GanttCalendarBackend {
  /** Returns the gantt-native calendar object for the given id. */
  getCalendar(calendarId: CalendarID): GanttNativeCalendar;
  /** Returns the global calendar (gantt's default). */
  getCalendar(): GanttNativeCalendar;
  /** Returns the task object — used to read calendar_id for inheritance walk. */
  getTask(id: TaskID): { calendar_id?: CalendarID; parent?: TaskID; [key: string]: unknown };
  /**
   * Config-level flags. `work_time === false` (gantt's default) means gantt
   * ignores all working-time rules and uses flat 24/7 arithmetic — see
   * `core/worktime/time_calculator.js:_getCalendar`. The registry mirrors that
   * by resolving every task to a flat calendar in that case.
   */
  config: { inherit_calendar?: boolean; work_time?: boolean };
  /**
   * Gantt's top-level, work-time-aware calculators. These route through
   * `TimeCalculator._getCalendar`, which swaps in the `NoWorkTimeCalendar`
   * (flat 24/7) strategy when `config.work_time === false`. Optional so stub
   * backends in unit tests need not provide them; only consulted when
   * `work_time` is disabled.
   */
  // Loose, all-optional param shapes so the various concrete gantt backends
  // (each declaring stricter `task` types) remain structurally assignable.
  calculateEndDate?(config: { start_date?: Date; duration?: number; unit?: TimeUnit; task?: any }): Date;
  calculateDuration?(config: { start_date?: Date; end_date?: Date; unit?: TimeUnit; task?: any }): number;
  getClosestWorkTime?(config: { date?: Date; dir?: "future" | "past"; unit?: TimeUnit; task?: any }): Date;
  /**
   * Gantt's full effective-calendar resolution
   * (`calendar_manager.getTaskCalendar`): task's own `calendar_property` →
   * resource calendars (`config.resource_calendars` + `resource_property`,
   * including the legacy format and `dynamic_resource_calendars` merging) →
   * group-mode-aware `inherit_calendar` walk over summary ancestors → global.
   * Optional so stub backends can omit it; without it the registry falls back
   * to its manual `calendar_id` + inherit walk, which does NOT see resource
   * calendars.
   */
  getTaskCalendar?(task: unknown): GanttNativeCalendar;
}

/** Synthetic id for the flat calendar used when `work_time === false`. */
const NO_WORK_TIME_CALENDAR_ID = "$no_work_time" as CalendarID;

/**
 * Build a `GanttNativeCalendar` that performs flat (24/7) arithmetic by
 * delegating to the gantt instance's top-level calculators. When
 * `config.work_time === false` those calculators use gantt's
 * `NoWorkTimeCalendar` strategy regardless of which calendar id is passed, so
 * the per-task calendar becomes irrelevant — every task shares this one.
 *
 * This mirrors gantt's own behavior: `gantt.calculateEndDate` /
 * `gantt.calculateDuration` honor `work_time`, but a calendar object's methods
 * (which the registry otherwise wraps directly) do not. Without this, the v2
 * engine would apply weekend/work-hour rules even when the user disabled
 * working time — producing wrong dates and slack vs v1.
 */
function makeNoWorkTimeNativeCalendar(host: GanttCalendarBackend): GanttNativeCalendar {
  return {
    id: NO_WORK_TIME_CALENDAR_ID,
    calculateEndDate: (cfg) => host.calculateEndDate!(cfg),
    calculateDuration: (cfg) => host.calculateDuration!(cfg),
    getClosestWorkTime: (cfg) => host.getClosestWorkTime!(cfg),
    isWorkTime: () => true
  };
}

/**
 * Structural shape of gantt's native calendar object. Adapter calls only
 * these methods.
 */
export interface GanttNativeCalendar {
  getClosestWorkTime(cfg: { date: Date; dir: "future" | "past"; unit?: TimeUnit; task?: unknown }): Date;
  calculateDuration(cfg: { start_date: Date; end_date: Date; unit?: TimeUnit; task?: unknown }): number;
  calculateEndDate(cfg: { start_date: Date; duration: number; unit?: TimeUnit; task?: unknown }): Date;
  isWorkTime?(date: Date): boolean;
  id?: CalendarID;
}

/**
 * Direction mapping per D-21. Engine vocabulary is "forward"/"backward";
 * gantt's `getClosestWorkTime` expects "future"/"past".
 */
function mapDirection(dir: SnapDirection): "future" | "past" {
  return dir === "forward" ? "future" : "past";
}

/**
 * `Calendar` implementation that delegates to a gantt native calendar.
 * Per D-22, the round-trip stability invariant is weakened: callers should
 * snap via `alignToWorkingBoundary` before relying on round-trip arithmetic.
 *
 * Phase D Day 8 perf optimization: per-instance memoization on
 * `addDuration` and `calculateDuration`. Each `GanttCalendar` lives for one
 * snapshot lifetime — when the snapshot rebuilds, a fresh `GanttCalendar`
 * (with empty caches) is constructed. The cache key is the input tuple
 * `(start_ms, duration, unit)`; cache size bounded at 4096 entries with
 * simple FIFO eviction. Targets the `mixed_calendars @ 10k` regression
 * (Phase D Day 8 entry in `perf-baseline.md`).
 */
const MEMO_CAPACITY = 4096;

export class GanttCalendar implements Calendar {
  readonly id: CalendarID;
  private readonly addDurationCache = new Map<string, number>();
  private readonly calcDurationCache = new Map<string, number>();

  constructor(private readonly inner: GanttNativeCalendar, calendarId: CalendarID) {
    this.id = calendarId;
  }

  addDuration(start: Date, duration: number, unit: TimeUnit): Date {
    const key = `${start.getTime()}|${duration}|${unit}`;
    const hit = this.addDurationCache.get(key);
    if (hit !== undefined) return new Date(hit);
    const result = this.inner.calculateEndDate({ start_date: start, duration, unit });
    if (this.addDurationCache.size >= MEMO_CAPACITY) {
      const firstKey = this.addDurationCache.keys().next().value;
      if (firstKey !== undefined) this.addDurationCache.delete(firstKey);
    }
    this.addDurationCache.set(key, result.getTime());
    return result;
  }

  calculateDuration(start: Date, end: Date, unit: TimeUnit): number {
    const key = `${start.getTime()}|${end.getTime()}|${unit}`;
    const hit = this.calcDurationCache.get(key);
    if (hit !== undefined) return hit;
    // NOTE: gantt's `argumentsHelper._getStartEndConfig` does NOT honor the
    // `unit` field on the object-form arguments — it only reads
    // (start_date, end_date, task) into `GetDurationArgument`, then falls
    // back to `gantt.config.duration_unit`. Positional invocation doesn't
    // help either (the constructor signature drops the unit slot). Engine
    // slack values therefore come back in `gantt.config.duration_unit`
    // (typically "day"), not minutes — the D-35 "engine in minutes" goal
    // requires a gantt API fix that's out of scope here. For now, the
    // `isCritical: totalSlack <= 0` threshold in `critical_path.ts` works
    // because both forward and latest passes use the same buggy unit.
    const result = this.inner.calculateDuration({ start_date: start, end_date: end, unit });
    if (this.calcDurationCache.size >= MEMO_CAPACITY) {
      const firstKey = this.calcDurationCache.keys().next().value;
      if (firstKey !== undefined) this.calcDurationCache.delete(firstKey);
    }
    this.calcDurationCache.set(key, result);
    return result;
  }

  snapToWorking(date: Date, direction: SnapDirection): Date {
    return this.inner.getClosestWorkTime({ date, dir: mapDirection(direction) });
  }

  alignToWorkingBoundary(date: Date, direction: SnapDirection): Date {
    // Per D-22: this is the snap-before-arithmetic helper. gantt's
    // getClosestWorkTime is its strongest snap primitive; we delegate.
    return this.inner.getClosestWorkTime({ date, dir: mapDirection(direction) });
  }

  isWorkingTime(date: Date): boolean {
    if (this.inner.isWorkTime) return this.inner.isWorkTime(date);
    // Defensive fallback — gantt builds vary on this API. The closest-work-
    // time check is "is this date already its own closest working time."
    const snapped = this.inner.getClosestWorkTime({ date, dir: "future" });
    return snapped.getTime() === date.getTime();
  }
}

/**
 * `CalendarRegistry` implementation. Resolves each task's calendar via
 * inherit_calendar (walks parent chain) and memoizes per task and per
 * calendar id.
 */
export class GanttCalendarRegistry implements CalendarRegistry {
  readonly default: Calendar;
  private readonly cache = new Map<CalendarID, Calendar>();
  private readonly perTaskCache = new Map<NormalizedTaskID, Calendar>();
  /**
   * When non-null, `work_time` is disabled: every task resolves to this single
   * flat calendar and per-task / inheritance resolution is skipped (it would
   * be a no-op since all calendars behave identically with working time off).
   */
  private readonly flatCalendar: Calendar | null;

  constructor(
    private readonly gantt: GanttCalendarBackend,
    private readonly hierarchy: HierarchyView
  ) {
    // gantt's default is `work_time: false`. In that mode gantt ignores
    // calendars entirely (flat arithmetic), so we must too — otherwise the
    // engine applies weekend/work-hour rules the user disabled. Requires the
    // top-level calculators on the backend; stub backends (unit tests) omit
    // them and never run with work_time disabled, so we fall through to the
    // per-task calendar path in that case.
    const workTimeDisabled =
      this.gantt.config.work_time === false &&
      typeof this.gantt.calculateEndDate === "function" &&
      typeof this.gantt.calculateDuration === "function" &&
      typeof this.gantt.getClosestWorkTime === "function";

    if (workTimeDisabled) {
      this.flatCalendar = new GanttCalendar(
        makeNoWorkTimeNativeCalendar(this.gantt),
        NO_WORK_TIME_CALENDAR_ID
      );
      this.default = this.flatCalendar;
      this.cache.set(NO_WORK_TIME_CALENDAR_ID, this.flatCalendar);
      return;
    }

    this.flatCalendar = null;
    const inner = this.gantt.getCalendar();
    const cid: CalendarID = (inner.id ?? "global") as CalendarID;
    this.default = new GanttCalendar(inner, cid);
    this.cache.set(cid, this.default);
  }

  resolveForTask(taskId: NormalizedTaskID): Calendar {
    if (this.flatCalendar) return this.flatCalendar;
    const cached = this.perTaskCache.get(taskId);
    if (cached) return cached;

    let resolved: Calendar | null = null;

    if (typeof this.gantt.getTaskCalendar === "function") {
      // Real gantt backend: delegate to the native resolver so every
      // effective-calendar source applies — `calendar_property`, resource
      // calendars (plain / legacy / dynamic merged) and the group-mode-aware
      // inherit walk. The manual walk below only sees `calendar_id`, so a
      // task whose calendar comes from `resource_calendars` would silently
      // schedule on the global calendar (lands on its own non-working days
      // and its engine end diverges from the write-back end, letting
      // successors start before the predecessor finishes).
      const inner = this.gantt.getTaskCalendar(this.getTask(taskId));
      if (inner) {
        const cid: CalendarID = (inner.id ?? "global") as CalendarID;
        let cal = this.cache.get(cid);
        if (!cal) {
          cal = new GanttCalendar(inner, cid);
          this.cache.set(cid, cal);
        }
        resolved = cal;
      }
    }

    if (!resolved) {
      // Stub-backend fallback: task's own calendar_id wins; otherwise
      // ancestors when inherit_calendar is on; otherwise default.
      let cur: NormalizedTaskID | null = taskId;
      const inherit = this.gantt.config.inherit_calendar !== false;
      while (cur !== null) {
        const taskObj = this.getTask(cur);
        const cid = taskObj?.calendar_id;
        if (cid !== undefined && cid !== null) {
          resolved = this.get(cid);
          break;
        }
        if (!inherit) break;
        cur = this.hierarchy.parentOf(cur);
      }
    }

    if (!resolved) resolved = this.default;
    this.perTaskCache.set(taskId, resolved);
    return resolved;
  }

  get(calendarId: CalendarID): Calendar {
    if (this.flatCalendar) return this.flatCalendar;
    const cached = this.cache.get(calendarId);
    if (cached) return cached;
    const inner = this.gantt.getCalendar(calendarId);
    if (!inner) {
      // Gantt returns null/undefined for missing calendars — fall back to
      // default rather than throwing (matches v1 leniency).
      return this.default;
    }
    const cal = new GanttCalendar(inner, calendarId);
    this.cache.set(calendarId, cal);
    return cal;
  }

  private getTask(taskId: NormalizedTaskID): { calendar_id?: CalendarID; parent?: TaskID; [key: string]: unknown } {
    for (const candidate of taskIdLookupCandidates(taskId)) {
      const task = this.gantt.getTask(candidate);
      if (task && (task.calendar_id !== undefined || task.parent !== undefined)) return task;
    }
    return this.gantt.getTask(normalizeTaskId(taskId));
  }
}
